import { useEffect, useMemo, useRef, useState } from "react";
import { useRecoilState, useRecoilValueLoadable, useSetRecoilState } from "recoil";

import { useOpenSnackbar } from "@hooks/useSnackbar";
import axios from "@utils/axios";
import { formatKstDate, formatKstDateTime } from "@utils/kst";
// material
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Divider,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { DataGridPro, GridColDef, GridSortModel } from "@mui/x-data-grid-pro";
import { Icon } from "@iconify/react";
import searchFill from "@iconify/icons-eva/search-fill";

import {
  ADMIN_USERS_DEFAULT_SORT,
  adminUsersActive,
  adminUsersLevel,
  adminUsersMembershipId,
  adminMembershipsQuery,
  adminUsersPage,
  adminUsersPageSize,
  adminUsersQuery,
  adminUsersQueryText,
  adminUsersSort,
  adminRefreshTick,
} from "@states/admin";
import SignupRequestPanel from "./SignupRequestPanel";
import MembershipDialog from "./MembershipDialog";
import { LEVEL_LABEL, LEVEL_ORDER } from "./levels";
import { AdminUserType } from "types/admin";
import { MembershipLevel } from "types/auth";

/**
 * 화면 필드명 → **DB 컬럼명**.
 *
 * 서버는 `orderBy` 를 `UserModel.__table__.columns` 로 검증하고, 모르는 값이면
 * 조용히 `created_at` 으로 되돌린다. 그래서 `createdAt` 을 그대로 보내면 정렬한
 * 것처럼 보이지만 실제로는 가입일 내림차순이 된다 — 여기서 바꿔서 보낸다.
 *
 * 여기 없는 컬럼은 정렬할 수 없다. `level` 은 유효 기간을 한국 날짜로 따져 서버가
 * 계산하는 값이고 `memberships` 는 조인해 이어 붙인 목록이라 SQL 한 줄로 줄을 세울
 * 수 없다. 그래서 그 둘은 `sortable: false` 다 — 누를 수 있게 두면 "눌렀는데 현재
 * 페이지만 바뀐다" 가 된다.
 */
const SORT_FIELD_TO_COLUMN: Record<string, string> = {
  email: "email",
  name: "name",
  isActive: "is_active",
  createdAt: "created_at",
  lastLoginAt: "last_login_at",
  // 컬럼이 아니라 계산값이다. 서버가 «유효한 멤버십 중 가장 높은 순위» 로 줄을 세운다.
  level: "level",
};

/** 등급 거르개의 선택지. 순위 낮은 것부터 놓아 화면의 계층과 같은 순서로 보인다. */
const LEVEL_OPTIONS = [
  { value: "", label: "전체 등급" },
  ...(Object.keys(LEVEL_ORDER) as MembershipLevel[])
    .sort((a, b) => LEVEL_ORDER[a] - LEVEL_ORDER[b])
    .map((level) => ({ value: level, label: LEVEL_LABEL[level].text })),
  { value: "none", label: "등급 없음" },
];

function errorText(error: any, fallback: string): string {
  return typeof error?.detail === "string" ? error.detail : fallback;
}

type Handlers = {
  onManage: (row: AdminUserType) => void;
  onRename: (row: AdminUserType) => void;
  onToggleActive: (row: AdminUserType) => void;
  busyId: string | null;
};

const buildColumns = ({ onManage, onRename, onToggleActive, busyId }: Handlers): GridColDef[] => [
  { field: "email", headerName: "이메일", flex: 1.4, minWidth: 220 },
  {
    field: "name",
    headerName: "이름",
    flex: 0.6,
    minWidth: 100,
    // 이름은 이제 **계정 주인을 가리는 값**이다. 본인인증을 처음 통과할 때 이 값과
    // 본인확인 결과를 대조하므로(`_same_person`), 다르면 그 사람은 **로그인 자체가
    // 막힌다.** 개명했거나 가입 때 다른 이름을 적은 사람이 여기 걸리는데, 세션이
    // 없으니 스스로 고칠 수 없다 — 풀어 줄 수 있는 것은 관리자뿐이다.
    renderCell: ({ row }) => (
      <Button
        size="small"
        onClick={() => onRename(row as AdminUserType)}
        sx={{ px: 0.5, minWidth: 0, textTransform: "none", color: "text.primary" }}
      >
        {(row as AdminUserType).name || "(이름 없음)"}
      </Button>
    ),
  },
  {
    field: "level",
    headerName: "등급",
    flex: 0.5,
    minWidth: 100,
    // 서버가 등급 «순위» 로 정렬한다. 글자순으로 세우면 관리자·베이직·엘리트·프로가
    // 되어 사람이 기대하는 계층과 무관해진다.
    // 거르기는 헤더 메뉴가 아니라 위의 선택 상자가 한다 — 화면 글자(«엘리트»)와
    // 서버 값(`elite`)이 달라 기본 거르개로는 아무것도 안 걸린다.
    filterable: false,
    renderCell: ({ value }) => {
      const level = value as MembershipLevel | null;
      if (!level) return <Chip size="small" label="없음" variant="outlined" />;
      const { text, color } = LEVEL_LABEL[level];
      return <Chip size="small" label={text} color={color} />;
    },
  },
  {
    field: "memberships",
    headerName: "멤버십",
    flex: 1.2,
    minWidth: 180,
    sortable: false,
    // 기간이 지난 멤버십도 행에는 들어 있다. 유효한 것만 보고 싶으면 등급 칸을 본다
    // (서버가 한국 날짜로 계산해서 `level` 로 준다).
    valueGetter: ({ row }) => (row.memberships ?? []).map((m) => m.name).join(", "),
  },
  {
    field: "isActive",
    headerName: "상태",
    flex: 0.5,
    minWidth: 120,
    // `is_active` 는 진짜 DB 컬럼이라 서버가 정렬할 수 있다. 비활성 계정을 모아 보는
    // 것이 이 화면에서 자주 하는 일이다.
    renderCell: ({ row }) => {
      const user = row as AdminUserType;
      return (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Switch
            size="small"
            checked={user.isActive}
            disabled={busyId === user.id}
            onChange={() => onToggleActive(user)}
            inputProps={{ "aria-label": `${user.email} 계정 활성 여부` }}
          />
          {user.isActive ? (
            <Chip size="small" label="활성" color="success" variant="outlined" />
          ) : (
            <Chip size="small" label="비활성" color="error" variant="outlined" />
          )}
        </Stack>
      );
    },
  },
  {
    field: "createdAt",
    headerName: "가입일",
    flex: 0.6,
    minWidth: 120,
    // 문자열을 자르지 않는다. 서버는 UTC 로 주므로 `.slice(0, 10)` 은 **UTC 날짜**를
    // 보여 준다 — 한국시간 오전 0~9시에 가입한 사람이 전날로 보였다.
    valueGetter: ({ value }) => formatKstDate(value),
  },
  {
    field: "lastLoginAt",
    headerName: "최종 로그인 (KST)",
    flex: 0.9,
    minWidth: 170,
    // 빈 칸이 아니라 «없음» 이라고 쓴다. 빈 칸은 «아직 안 불러왔나» 로도 읽힌다.
    // 이 칸이 생기기 전부터 있던 사람과 한 번도 안 들어온 사람이 여기 든다.
    renderCell: ({ value }) => {
      const text = formatKstDateTime(value as string | null);
      return text ? (
        <span>{text}</span>
      ) : (
        <Typography variant="caption" sx={{ color: "text.disabled" }}>
          기록 없음
        </Typography>
      );
    },
  },
  {
    field: "actions",
    headerName: "",
    width: 130,
    sortable: false,
    filterable: false,
    renderCell: ({ row }) => (
      <Button size="small" variant="outlined" onClick={() => onManage(row as AdminUserType)}>
        멤버십 설정
      </Button>
    ),
  },
];

export default function UserPanel() {
  const loadable = useRecoilValueLoadable(adminUsersQuery);
  const [page, setPage] = useRecoilState(adminUsersPage);
  const [pageSize, setPageSize] = useRecoilState(adminUsersPageSize);
  const setQueryText = useSetRecoilState(adminUsersQueryText);
  const [sort, setSort] = useRecoilState(adminUsersSort);
  const [level, setLevel] = useRecoilState(adminUsersLevel);
  const [membershipId, setMembershipId] = useRecoilState(adminUsersMembershipId);
  const [isActive, setIsActive] = useRecoilState(adminUsersActive);
  // 그룹 목록은 «멤버십 관리» 탭이 이미 읽는 것과 **같은 셀렉터**다. 두 번 부르지 않는다.
  //
  // `useRecoilValue` 가 아니라 loadable 인 이유 — 이 패널은 라우트에서 Suspense 로
  // 감싸여 있지 않다. 서스펜드하면 목록·검색·가입신청까지 통째로 사라진다.
  // 아직 안 온 동안에는 «전체 멤버십» 만 있는 드롭다운을 보여 준다.
  const groupsLoadable = useRecoilValueLoadable(adminMembershipsQuery);
  const groups = useMemo(
    () => (groupsLoadable.state === "hasValue" ? groupsLoadable.contents : []),
    [groupsLoadable.state, groupsLoadable.contents],
  );
  const [draft, setDraft] = useState("");
  const [managing, setManaging] = useState<AdminUserType | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // 끄는 것은 되돌리기 어렵다 — 그 사람은 바로 로그인이 막힌다. 한 번 물어본다.
  // 켜는 것은 묻지 않는다. 되돌리기 쉽고, 잠긴 사람을 풀어 주는 것이 목적이다.
  const [confirming, setConfirming] = useState<AdminUserType | null>(null);
  /** 이름을 고치는 중인 사람. 잠긴 계정을 풀어 주는 유일한 길이다. */
  const [renaming, setRenaming] = useState<AdminUserType | null>(null);
  const [newName, setNewName] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  const bumpRefresh = useSetRecoilState(adminRefreshTick);
  const openSnackbar = useOpenSnackbar();

  const data = loadable.state === "hasValue" ? loadable.contents : undefined;

  // 서버 페이지네이션이라 로딩 중에 `rowCount` 가 undefined 가 되면 그리드가 페이지 수를
  // 잃고 깜빡인다. 마지막으로 받은 값을 들고 있는다 (백테스트 이력 화면과 같은 처리).
  const [rowCount, setRowCount] = useState(0);
  useEffect(() => {
    if (data?.total !== undefined) setRowCount(data.total);
  }, [data?.total]);

  const rows = useMemo<AdminUserType[]>(() => data?.items ?? [], [data]);

  // 부여·회수 뒤 목록이 다시 오면 열려 있는 다이얼로그도 새 값을 봐야 한다.
  // 안 그러면 "회수" 를 눌렀는데 다이얼로그에는 그대로 남아 있다.
  const managingRow = useMemo(
    () => (managing ? rows.find((row) => row.id === managing.id) ?? managing : null),
    [managing, rows],
  );

  // 고르고 있던 그룹이 «멤버십 관리» 탭에서 지워지면, 화면의 드롭다운은 값을 못 찾아
  // «전체 멤버십» 로 보이는데 요청에는 그 id 가 계속 실린다. 결과는 늘 0건이고,
  // 관리자에게는 «전체인데 아무도 없다» 로 보인다 — 이유를 알 방법이 없다.
  // 목록이 실제로 도착했을 때만 확인한다. 로딩 중의 빈 배열로 되돌리면 방금 고른
  // 것을 지워 버린다.
  useEffect(() => {
    if (groupsLoadable.state !== "hasValue") return;
    if (membershipId === null) return;
    if (groups.some((group) => group.id === membershipId)) return;
    setMembershipId(null);
  }, [groupsLoadable.state, groups, membershipId, setMembershipId]);

  const search = () => {
    setPage(0); // 2페이지에서 검색하면 결과가 한 페이지뿐일 때 빈 화면이 나온다
    setQueryText(draft);
  };

  const setActive = async (target: AdminUserType, isActive: boolean) => {
    setBusyId(target.id);
    try {
      await axios.patch(`users/${target.id}/active`, { isActive });
      openSnackbar(
        `${target.email} 계정을 ${isActive ? "활성화" : "비활성화"}했습니다.`,
        "success",
      );
    } catch (error: any) {
      // 서버가 막는 두 가지(자기 계정 400 · 마지막 관리자 409)는 사람이 읽을 문장을
      // 그대로 준다. 그 밖의 모양일 때만 대신할 문구를 쓴다 — `detail` 이 문자열이
      // 아닐 수 있고(422 는 객체 배열) 그대로 그리면 화면이 죽는다.
      openSnackbar(errorText(error, "계정 상태를 바꾸지 못했습니다."), "error");
    } finally {
      setBusyId(null);
      bumpRefresh((tick) => tick + 1);
    }
  };

  const toggleActive = (target: AdminUserType) => {
    if (target.isActive) {
      setConfirming(target);
      return;
    }
    void setActive(target, true);
  };

  // `toggleActive` 는 매 렌더 새로 만들어진다(`useOpenSnackbar()` 가 매번 새 함수를
  // 돌려줘서 `useCallback` 으로도 안정화되지 않는다). 의존성에 넣으면 매 렌더마다
  // columns 가 새로 만들어져 그리드가 통째로 다시 그려진다. ref 로 최신 것을 가리키게
  // 하면 오래된 클로저도 없고 재생성도 없다 (멤버십 관리와 같은 방식).
  /**
   * 이름을 고친다. **잠긴 계정을 풀어 주는 길이다.**
   *
   * 본인인증 지문을 처음 등록할 때 계정의 이름과 본인확인 결과를 대조하므로
   * (서버 `_same_person`), 이름이 다르면 로그인 자체가 막힌다. 그 사람은 세션이
   * 없어 스스로 못 고친다.
   */
  const rename = async () => {
    if (!renaming) return;
    setRenameBusy(true);
    try {
      await axios.patch(`users/${renaming.id}/name`, { name: newName.trim() });
      openSnackbar("이름을 바꿨습니다.", "success");
      setRenaming(null);
      bumpRefresh((tick) => tick + 1);
    } catch (error: any) {
      openSnackbar(
        typeof error?.detail === "string" ? error.detail : "이름을 바꾸지 못했습니다.",
        "error",
      );
    } finally {
      setRenameBusy(false);
    }
  };

  const toggleRef = useRef(toggleActive);
  toggleRef.current = toggleActive;

  const columns = useMemo(
    () =>
      buildColumns({
        onManage: setManaging,
        onRename: (row) => {
          setRenaming(row);
          setNewName(row.name ?? "");
        },
        onToggleActive: (row) => toggleRef.current(row),
        busyId,
      }),
    [busyId],
  );

  // 그리드는 화면 필드명으로 말하고 서버는 DB 컬럼명으로 듣는다. 양방향으로 번역한다.
  const sortModel = useMemo<GridSortModel>(() => {
    const field = Object.keys(SORT_FIELD_TO_COLUMN).find(
      (key) => SORT_FIELD_TO_COLUMN[key] === sort.field,
    );
    return field ? [{ field, sort: sort.order }] : [];
  }, [sort]);

  const changeSort = (model: GridSortModel) => {
    // 정렬을 바꾸면 순서가 통째로 달라진다. 3페이지에 머무르면 방금 누른 것과
    // 상관없는 구간을 보게 되므로 첫 페이지로 돌린다.
    setPage(0);
    const next = model[0];
    const column = next ? SORT_FIELD_TO_COLUMN[next.field] : undefined;
    // 세 번째 클릭은 정렬 해제다. 서버는 무정렬을 모르므로 기본값으로 돌아간다.
    setSort(column && next?.sort ? { field: column, order: next.sort } : ADMIN_USERS_DEFAULT_SORT);
  };

  return (
    <Stack spacing={3}>
      {/* 가입 신청을 사용자 목록 **위에** 둔다. 관리자 알림 메일이 이 탭으로 링크하고,
          여기 온 이유가 대개 "승인해 달라" 이기 때문이다. */}
      <SignupRequestPanel />

      <Divider />

      <Typography variant="subtitle1">사용자</Typography>
      <Stack direction="row" spacing={2}>
        <TextField
          size="small"
          placeholder="이메일 또는 이름"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") search();
          }}
          onBlur={search}
          sx={{ width: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon icon={searchFill} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="등급"
          value={level}
          onChange={(event) => {
            // 검색과 같은 이유로 첫 페이지로 돌린다 — 3페이지에서 거르면
            // 결과가 두 페이지뿐일 때 빈 화면이 나온다.
            setPage(0);
            setLevel(event.target.value);
          }}
          SelectProps={{ native: true }}
          // ★ 라벨을 **늘 위로** 올린다. 값이 `""`(전체)이면 MUI 는 칸이 비었다고 보고
          // 라벨을 안쪽에 둔다. 그런데 네이티브 셀렉트는 «전체 등급» 을 그리므로 두
          // 글자가 겹쳐 보였다(2026-09-23 제보).
          InputLabelProps={{ shrink: true }}
          sx={{ width: 180 }}
        >
          {LEVEL_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </TextField>
        {/* 계정 상태. **서버로 거른다.** 이 칸이 없던 동안 표 머리의 컬럼 필터로 «비활성만»
            을 걸면 지금 페이지 25명 안에서만 걸렸다(f2-0) — 그 필터는 아래에서 껐다. */}
        <TextField
          select
          size="small"
          label="상태"
          value={isActive === null ? "" : String(isActive)}
          onChange={(event) => {
            setPage(0);
            setIsActive(event.target.value === "" ? null : event.target.value === "true");
          }}
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 130 }}
        >
          <option value="">전체 상태</option>
          <option value="true">활성</option>
          <option value="false">비활성</option>
        </TextField>
        {/* 등급과 **나란히 두되 라벨로 가른다.** 둘은 다른 축이다 — 등급은
            «무엇을 할 수 있나», 멤버십은 «어느 그룹에 속했나» 다. 같은 `pro` 라도
            «ETF 투자바이블» 과 «퀀트 입문반» 은 다른 그룹이고, 바로 옆 «멤버십»
            칸이 보여 주는 것이 그 그룹이다.

            기간이 끝난 그룹도 잡힌다 — «멤버십 관리» 탭의 인원수와 기준을 맞췄다. */}
        <TextField
          select
          size="small"
          label="멤버십"
          value={membershipId === null ? "" : String(membershipId)}
          onChange={(event) => {
            setPage(0);
            setMembershipId(event.target.value === "" ? null : Number(event.target.value));
          }}
          SelectProps={{ native: true }}
          // 등급 칸과 같은 이유로 라벨을 늘 위로 올린다.
          InputLabelProps={{ shrink: true }}
          sx={{ width: 220 }}
        >
          <option value="">전체 멤버십</option>
          {groups.map((group) => (
            <option key={group.id} value={String(group.id)}>
              {group.name}
            </option>
          ))}
        </TextField>
      </Stack>
      {loadable.state === "hasError" && (
        // 조회가 실패하면 빈 표만 그려져 «사용자가 없다» 로 읽혔다(f2-3). 실패라고 말한다.
        <Alert severity="error">
          사용자 목록을 불러오지 못했습니다. 새로고침하거나 잠시 후 다시 시도해주세요.
        </Alert>
      )}
      <Box sx={{ height: 560, width: "100%" }}>
        <DataGridPro
          rows={rows}
          rowCount={rowCount}
          loading={loadable.state === "loading"}
          columns={columns}
          pagination
          paginationMode="server"
          sortingMode="server"
          // ★ 컬럼 필터를 **끈다.** 켜 두면 머리 메뉴의 «Filter» 가 서버로 안 가고 지금 화면의
          //   25명 안에서만 거른다 — 1,615명 중 25명만 보고 «없다» 가 된다(2026-09-23 f2-0,
          //   MUI 기본 filterMode 가 client). 거르기는 위의 검색·상태·등급·멤버십 칸이 서버로 한다.
          disableColumnFilter
          // 정렬 해제 상태를 만들지 않는다. 서버에는 "무정렬" 이 없어서 해제하면
          // 기본 정렬로 돌아가는데, 기본 정렬이 걸린 칸(가입일·실행일시)을 누르면
          // "해제 → 기본값" 이라 **아무 일도 안 일어난다.** 눌렀는데 반응이 없는 것은
          // 고장으로 보인다. 오름/내림만 오가게 한다.
          sortingOrder={["asc", "desc"]}
          sortModel={sortModel}
          onSortModelChange={changeSort}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          rowsPerPageOptions={[25, 50, 100]}
          disableSelectionOnClick
          density="compact"
          getRowId={(row) => row.id}
        />
      </Box>

      <MembershipDialog user={managingRow} onClose={() => setManaging(null)} />

      <Dialog open={confirming !== null} onClose={() => setConfirming(null)}>
        <DialogTitle>계정을 비활성화할까요?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <b>{confirming?.email}</b>
            <br />
            이 계정은 <b>바로 로그인할 수 없게 됩니다.</b> 이미 로그인해 있어도 다음
            요청부터 막힙니다. 다시 켜는 것은 언제든 가능합니다.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)}>취소</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              const target = confirming;
              setConfirming(null);
              if (target) void setActive(target, false);
            }}
          >
            비활성화
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={renaming !== null} onClose={() => setRenaming(null)} fullWidth maxWidth="xs">
        <DialogTitle>이름 수정</DialogTitle>
        <DialogContent>
          <DialogContentText component="div" sx={{ mb: 2 }}>
            <b>{renaming?.email}</b>
            <br />
            이 이름은 <b>본인인증 결과와 대조됩니다.</b> 다르면 그분은 로그인할 수
            없습니다 — 개명하셨거나 가입 때 다른 이름을 적으신 경우입니다.
            <b> 주민등록상 이름</b>으로 맞춰주세요.
          </DialogContentText>
          <TextField
            fullWidth
            autoFocus
            label="이름"
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            inputProps={{ maxLength: 36 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenaming(null)}>취소</Button>
          <LoadingButton
            variant="contained"
            loading={renameBusy}
            disabled={!newName.trim() || newName.trim() === renaming?.name}
            onClick={() => void rename()}
          >
            저장
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
